/**
 * DateTimeRangePicker — a start and an end, each a date and a time, picked on
 * one inline range calendar with quick picks, plus typed time fields and a
 * "Runs N days" summary. Its layout follows its own width, in `date-time-range.css`.
 *
 * Values are wall-clock dates and times with no zone; the caller decides what
 * zone they are in and says so through `zoneNote`. Must sit inside
 * `.nb-dashboards`, whose tokens it is drawn with.
 */
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { type DateKey } from './date-keys';
import { isRangeInOrder, pickDay, type DateTimeRange, type RangeEnd } from './date-time-range';
import { RangeCalendar } from './RangeCalendar';
import { RangeEndFields } from './RangeEndFields';
import { RangeSummary } from './RangeSummary';

import './date-time-range.css';

export interface DateTimeRangePickerProps {
  value: DateTimeRange;
  onChange: (range: DateTimeRange) => void;
  /** Today on the same wall calendar; earlier days cannot be picked. */
  today: DateKey;
  /** What the range is, for the "Pick the first day of …" hint. */
  noun?: string;
  zoneNote?: string;
  /**
   * Whether the end is after the start, when the caller judges that on real
   * instants (a time-zone change can make the two disagree). Defaults to
   * comparing the wall clocks.
   */
  inOrder?: boolean;
}

export function DateTimeRangePicker(p: DateTimeRangePickerProps) {
  const [editing, setEditing] = useState<RangeEnd>('start');
  const outOfOrder = !(p.inOrder ?? isRangeInOrder(p.value));
  const { start, end } = p.value;

  const pick = (day: DateKey) => {
    const result = pickDay(p.value, editing, day);
    p.onChange(result.range);
    setEditing(result.next);
  };

  return (
    <div className="dtr">
      <div className="dtr-body">
        <div className="dtr-ends">
          <RangeEndFields
            which="start"
            point={start}
            active={editing === 'start'}
            invalid={false}
            onAim={() => setEditing('start')}
            onTime={(minutes) => p.onChange({ start: { ...start, minutes }, end })}
          />
          <span aria-hidden="true" className="dtr-arrow">
            <ChevronRight size={16} />
          </span>
          <RangeEndFields
            which="end"
            point={end}
            active={editing === 'end'}
            invalid={outOfOrder}
            onAim={() => setEditing('end')}
            onTime={(minutes) => p.onChange({ start, end: { ...end, minutes } })}
          />
        </div>
        <RangeCalendar
          range={p.value}
          editing={editing}
          today={p.today}
          noun={p.noun ?? 'range'}
          onPickDay={pick}
          onRange={p.onChange}
        />
        <RangeSummary range={p.value} inOrder={!outOfOrder} zoneNote={p.zoneNote} />
      </div>
    </div>
  );
}
